<template>
  <select
    v-model="localValue"
    :class="mergedClasses"
    :disabled="disabled"
    :multiple="multiple"
    :size="htmlSize"
    v-bind="passthroughAttrs"
    @change="handleChange"
  >
    <option
      v-for="option in normalizedOptions"
      :key="String(option.value)"
      :value="option.value"
      :disabled="option.disabled"
    >
      {{ option.text }}
    </option>
  </select>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'HdFormSelect',

  inheritAttrs: false,

  props: {
    value: {
      default: null
    },
    modelValue: {
      default: undefined
    },
    options: {
      type: Array,
      default: () => []
    },
    valueField: {
      type: String,
      default: 'value'
    },
    textField: {
      type: String,
      default: 'text'
    },
    size: {
      type: String,
      default: null,
      validator: (value) => value == null || ['sm', 'lg'].includes(value)
    },
    selectSize: {
      type: [Number, String],
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    state: {
      type: [Boolean, String],
      default: null
    }
  },

  emits: ['input', 'update:modelValue', 'change'],

  data() {
    return {
      localValue: null
    };
  },

  created() {
    this.localValue = this.resolveLocalValue(this.boundValue);
  },

  computed: {
    boundValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    normalizedOptions() {
      return (this.options || []).map((option) => {
        if (option == null) return null;

        if (typeof option === 'string' || typeof option === 'number') {
          return { value: option, text: String(option), disabled: false };
        }

        const value = option[this.valueField] ?? option.value;
        const text = option[this.textField] ?? option.text ?? String(value);

        return {
          value,
          text,
          disabled: !!option.disabled
        };
      }).filter(Boolean);
    },

    selectClasses() {
      const classes = ['custom-select'];

      if (this.size === 'sm') classes.push('custom-select-sm');
      if (this.size === 'lg') classes.push('custom-select-lg');
      if (this.state === true) classes.push('is-valid');
      if (this.state === false) classes.push('is-invalid');

      return classes;
    },

    htmlSize() {
      if (this.selectSize == null || this.selectSize === '') return null;

      return Number(this.selectSize);
    },

    mergedClasses() {
      return [this.$attrs.class, this.selectClasses].flat().filter(Boolean);
    },

    passthroughAttrs() {
      const { class: _class, ...attrs } = this.$attrs;

      return attrs;
    }
  },

  watch: {
    boundValue(newValue) {
      this.syncLocalValue(newValue);
    },

    normalizedOptions() {
      this.syncLocalValue(this.boundValue);
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.syncLocalValue(this.boundValue);
    });
  },

  methods: {
    resolveLocalValue(value) {
      if (this.multiple) {
        if (!Array.isArray(value)) return [];

        return value.map((entry) => this.coerceValue(entry));
      }

      if (value == null || value === '') return value;

      return this.coerceValue(value);
    },

    syncLocalValue(value = this.boundValue) {
      const resolved = this.resolveLocalValue(value);

      if (this.valuesEqual(resolved, this.localValue)) return;

      this.localValue = resolved;
    },

    handleChange(event) {
      const value = this.readValue(event);

      if (!this.valuesEqual(value, this.localValue)) {
        this.localValue = value;
      }

      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },

    readValue(event) {
      if (this.multiple) {
        return Array.from(event.target.selectedOptions, (option) =>
          this.coerceValue(option.value)
        );
      }

      return this.coerceValue(event.target.value);
    },

    coerceValue(rawValue) {
      const match = this.normalizedOptions.find(
        (option) => String(option.value) === String(rawValue)
      );

      return match ? match.value : rawValue;
    },

    valuesEqual(left, right) {
      if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) return false;

        if (left.length !== right.length) return false;

        return left.every((entry, index) => String(entry) === String(right[index]));
      }

      return String(left) === String(right);
    }
  }
});
</script>
